import _ from 'lodash';

import {
  ISSUE_TYPES,
  MEDICATION_STATUS,
} from 'components/HealthRecords/AppointmentNotes/NewAppointmentNote/constants';
import CPPUtils from 'services/CPPUtils';
import moment from 'moment-timezone';
import Bugsnag from '@bugsnag/js';
import MiscUtils from 'services/MiscUtils';
import AdminUtils from 'services/AdminUtils';
import PhysicianUtils from 'services/PhysicianUtils';
import CalendarUtils from 'services/CalendarUtils';
import { runSavedPromptTyped } from 'thunks/runSavedPromptTyped';
import { AppointmentTypeEnum } from 'generated/api';
import type {
  Admin,
  Physician,
  AppointmentTranscriptionMessage,
  PatientDocument,
  Appointment,
  AppointmentTranscription,
  Immunization,
  Prescription,
  ReferralRequest,
  AppointmentNote,
  AdministeredDrug,
  PatientIssue,
  PatientMedication,
  HistoryIssue,
  ImmunizationValue,
  AdministeredDrugValue,
  HealthRecords,
  TaskComment,
  Reminder,
} from 'generated/api';
import { PatientData } from '../careand-redux/slices/healthRecords';
import { Transcription } from '../careand-redux/slices/ui/ui';

const OPEN_BRACKET = '{';
const CLOSE_BRACKET = '}';

class AIUtils {
  getAssistantName(): string {
    return 'Carey (AI Assistant)';
  }

  *generateJSONSections(str: string, reverse = false): Generator<any, undefined, undefined> {
    const searchTerm = 'JSON(';
    const indexes: number[] = [];

    // Find all occurrences of "JSON("
    for (let i = 0; i < str.length; i++) {
      if (str.substring(i, i + searchTerm.length) === searchTerm) {
        indexes.push(i);
      }
    }

    if (reverse) {
      indexes.reverse();
    }

    // Iterate through each JSON() section
    for (const startIdx of indexes) {
      const contentStart = startIdx + searchTerm.length;

      // Find the matching closing parenthesis
      let depth = 1;
      let endIdx = -1;

      for (let i = contentStart; i < str.length; i++) {
        if (str.charAt(i) === '(') {
          depth++;
        } else if (str.charAt(i) === ')') {
          depth--;
          if (depth === 0) {
            endIdx = i;
            break;
          }
        }
      }

      if (endIdx === -1) {
        continue; // No matching closing parenthesis found
      }

      const jsonContent = str.slice(contentStart, endIdx);

      try {
        const parsed = JSON.parse(jsonContent);
        yield parsed;
      } catch (err) {
        // Invalid JSON, skip to next section
        continue;
      }
    }

    return undefined;
  }

  /**
   * finds JSON() sections in a string and returns the first match
   * for performance reasons it searches backwards (it's more often that we need the last matches)
   */
  findJSONSection(str: string, predicate?: (elem: any) => boolean, reverse = true): any {
    const gen = this.generateJSONSections(str, reverse);
    for (const elem of gen) {
      if (!!elem && (!predicate || predicate(elem))) {
        return elem;
      }
    }
  }

  /**
   * tries to parse a JSON Object
   * skipping any llm text before or after the object
   * @param {string} str
   */
  parseJsonObject(str: string): any {
    if (!str || typeof str !== 'string') {
      return undefined;
    }
    const startIdx = str.indexOf('{');
    const stack = [OPEN_BRACKET];
    let endIdx = -1;
    for (let i = startIdx + 1; i < str.length; i++) {
      switch (str.charAt(i)) {
        case OPEN_BRACKET:
          stack.push(OPEN_BRACKET);
          break;
        case CLOSE_BRACKET:
          if (_.last(stack) === OPEN_BRACKET) {
            stack.pop();
          }
          break;
        default:
          break;
      }
      // console.log('-', i, str.charAt(i), stack)
      if (stack.length === 0) {
        endIdx = i;
        break;
      }
    }
    const jsonStr = str.slice(startIdx, endIdx + 1);
    // console.log('=', startIdx, endIdx, `"${jsonStr}"`)

    try {
      return JSON.parse(jsonStr);
    } catch (err) {
      return undefined;
    }
  }

  cleanBlock(text: string, blockType = 'json'): string {
    const regex = new RegExp(`\`\`\`(?:${blockType})?(.*?)\`\`\``, 'gs');
    const match = regex.exec(text);
    return match ? match[1]!.trim() : text.trim();
  }

  // AI-drafted replies tend to use dashes mid-sentence; replace them with commas
  // before inserting into the compose box. Line-start dashes (bullets) are kept.
  replaceDashesWithCommas(text: string): string {
    return text.replace(/[ \t]*—[ \t]*/g, ', ').replace(/[ \t]+[–-][ \t]+/g, ', ');
  }

  parseImmunizationsExtraction(extraction: any[]): any[] {
    const DATE_FORMAT = 'YYYY/MM/DD';
    return extraction.map((val) => ({
      ...val,
      given_date: val.given_date
        ? moment(val.given_date, DATE_FORMAT).add(12, 'hours').unix()
        : undefined,
      expiry_date: val.expiry_date
        ? moment(val.expiry_date, DATE_FORMAT).add(12, 'hours').unix()
        : undefined,
    }));
  }

  async runNotePrompt(
    patientId: string,
    name: string,
    note: string,
    existingItems?: string,
    additionalFields: Record<string, any> = {},
  ): Promise<any[]> {
    const fields = { note, existingItems, ...additionalFields };
    try {
      return await (runSavedPromptTyped as any)(name, fields, { patientId });
    } catch (err) {
      Bugsnag.notify(err as Error);
      return [];
    }
  }

  async getAutofilledMetrics(patientId: string, note: string): Promise<any[]> {
    const json = await this.runNotePrompt(patientId, 'Note Extraction - Metrics', note);
    return json.filter(({ value }) => value);
  }

  async getPrefilledPreventativeScreenings(patientId: string, note: string): Promise<any[]> {
    const json = await this.runNotePrompt(
      patientId,
      'Note Extraction - Preventive Screenings',
      note,
    );
    return json.map((value) => {
      return { value };
    });
  }

  async getAutofilledAllergies(
    patientId: string,
    note: string,
    records?: PatientData,
  ): Promise<any[]> {
    const allergies = CPPUtils.getActiveHealthRecords(records?.healthRecords, 'allergies');
    const existingItems = `${allergies.map((i: any) => i).join(', ')}` || 'N/A';
    const json = await this.runNotePrompt(
      patientId,
      'Note Extraction - Allergies',
      note,
      existingItems,
    );
    return json.map((value) => {
      return { value };
    });
  }

  async getAutofilledActiveIssues(
    patientId: string,
    note: string,
    records?: PatientData,
  ): Promise<any[]> {
    let values;
    try {
      const issues = (records?.healthRecords?.active_issues || [])
        .filter((issue) => CPPUtils.isHealthRecordItemEnabled(issue))
        .map((issue) => issue.value.name);

      const existingItems = `${issues.map((i) => i).join(', ')}` || 'N/A';

      let json = await this.runNotePrompt(
        patientId,
        'Note Extraction - Active Issues',
        note,
        existingItems,
      );

      values = _.orderBy(json, ['name'], ['asc']).map((value) => {
        return value;
      });
    } catch (err) {
      values = [];
    }

    return values.map((value) => {
      return { value };
    });
  }

  async getAutofilledMedicalIssues(
    patientId: string,
    note: string,
    records?: PatientData,
  ): Promise<any[]> {
    let values;
    try {
      const issues = (records?.healthRecords?.medical_issues || [])
        .filter((issue) => CPPUtils.isHealthRecordItemEnabled(issue))
        .map((issue) => issue.value.name);

      const existingItems = `${issues.map((i) => i).join(', ')}` || 'N/A';

      let json = await this.runNotePrompt(
        patientId,
        'Note Extraction - Issues',
        note,
        existingItems,
      );

      values = _.orderBy(json, ['issue_type', 'name'], ['asc', 'asc']).map((value) => {
        value.issue_type = ISSUE_TYPES[value.issue_type as keyof typeof ISSUE_TYPES];
        return value;
      });
    } catch (err) {
      values = [];
    }

    return values.map((value) => {
      return { value };
    });
  }

  /**
   * Formats a transcription for LLM prompts with speaker labels that reflect
   * how they were actually produced for the appointment's modality:
   * - Telemedicine (type Video, incl. Phone method): labels are channel-based
   *   (Staff = practitioner side, Patient = patient side) — reliable.
   * - Single-label transcripts (voice memos, collapsed diarization): the label
   *   carries no information (solo recordings store every message as
   *   'Patient') — omitted entirely.
   * - In-person diarized conversations: labels are diarizer cluster indices
   *   ('0' → Staff), not verified roles — renamed to Speaker A/B with a caveat
   *   so the model attributes by content instead of trusting the label.
   */
  convertTranscriptionToText(
    transcription?: AppointmentTranscription,
    appointment?: Appointment,
  ): string {
    const messages = transcription?.messages;
    if (!messages || messages.length === 0) {
      return '';
    }

    if (appointment?.type === AppointmentTypeEnum.Video) {
      return [
        '[Speaker labels are audio-channel-based: Staff = the practitioner side, Patient = the patient side of the call (which may include family members or interpreters).]',
        ...messages.map(({ from, text }) => `${from}: ${text}`),
      ].join('\n');
    }

    const labels = new Set(messages.map((m) => m.from));
    if (labels.size <= 1) {
      return [
        '[Single-speaker or undiarized recording — speaker attribution is unavailable. If this is a conversation, identify speakers from content.]',
        ...messages.map(({ text }) => text),
      ].join('\n');
    }

    return [
      '[In-person recording: Speaker A/Speaker B are automatic diarization guesses, NOT verified roles. Determine who is the practitioner and who is the patient from what is said; if a label contradicts the content, trust the content.]',
      ...messages.map(
        ({ from, text }) => `${from === 'Staff' ? 'Speaker A' : 'Speaker B'}: ${text}`,
      ),
    ].join('\n');
  }

  async getAutofilledHistory(
    patientId: string,
    note: string,
    records?: PatientData,
    historyType?: string,
  ): Promise<any[]> {
    let values;
    try {
      const issues = (records?.healthRecords?.history || [])
        .filter(
          (issue) =>
            CPPUtils.isHealthRecordItemEnabled(issue) && issue.value.history_type === historyType,
        )
        .map((issue) => issue.value.details);

      const existingItems = `${issues.map((i) => i).join(', ')}` || 'N/A';

      const json = await this.runNotePrompt(
        patientId,
        'Note Extraction - History',
        note,
        existingItems,
        { historyType },
      );

      values = _.orderBy(json, ['name'], ['asc']).map((value) => {
        value.history_type = historyType;
        return value;
      });
    } catch (err) {
      values = [];
    }

    return values.map((value) => {
      return { value };
    });
  }

  async getAutofilledLifestyle(patientId: string, note: string): Promise<any[]> {
    let values = await this.runNotePrompt(patientId, 'Note Extraction - Lifestyle', note);
    return values.map((value) => {
      return { value };
    });
  }

  cleanDocuments(documents: PatientDocument[] = []): any[] {
    return documents.map((doc) => {
      return MiscUtils.cleanObject({
        ...doc,
        template_metadata: undefined,
      });
    });
  }

  async getAutofilledMedications(
    patientId: string,
    note: string,
    records?: PatientData,
  ): Promise<any[]> {
    let values;
    try {
      const medications = (records?.healthRecords?.medications || [])
        .filter((medication) => CPPUtils.isHealthRecordItemEnabled(medication))
        .map(
          (medication) => medication.value.prescription_medication?.name || medication.value.name,
        );

      const existingItems = `${medications.map((i) => `- ${i}`).join('\n')}` || 'N/A';

      const json = await this.runNotePrompt(
        patientId,
        'Note Extraction - Medications',
        note,
        existingItems,
      );

      values = json.map((val) => {
        return {
          ...val,
          status: MEDICATION_STATUS.continue.name,
        };
      });
    } catch (e) {
      values = [];
    }
    return values.map((value) => {
      return { value };
    });
  }

  getStaffName(staffId: string, admins: Admin[] = [], physicians: Physician[] = []): string {
    const admin = admins.find(({ id }) => id === staffId);
    const physician = physicians.find(({ id }) => id === staffId);
    let name: string | undefined = staffId;
    if (admin) name = AdminUtils.getName(admin);
    else if (physician) name = PhysicianUtils.getName(physician);
    return name || staffId;
  }

  getCommentsString(comments: TaskComment[]): string[] {
    return comments.map(({ author_id, date, text }) => {
      return `${this.getStaffName(author_id)} (${CalendarUtils.fromNow(date)}): ${text}`;
    });
  }

  convertDocument(doc: PatientDocument, index = 0): string {
    let str = `${index + 1}. ${doc.title} [${doc.document_type}], ${CalendarUtils.fromNow(
      doc.date!,
    )}`;

    if (doc.comments) str += `\n  - Patient-facing comments: ${doc.comments}`;

    let otherComments = [...(doc.referral_comments || []), ...(doc.general_comments || [])];

    if (otherComments.length > 0) {
      str += `\n  - Private comments:\n`;
      str += this.getCommentsString(otherComments)
        .map((text) => `    - ${text}`)
        .join('\n');
    }
    return str;
  }

  convertReminder(reminder: Reminder): string {
    return `Patient Reminder (${
      (CalendarUtils.format(reminder.date), CalendarUtils.fromNow(reminder.date))
    }) - ${reminder.message}`;
  }

  convertNote({ value, author_id }: AppointmentNote): string {
    return `Appointment Note (${CalendarUtils.format(value.date)}, ${CalendarUtils.fromNow(
      value.date,
    )}), Author ID: ${author_id}\n\n${value.value}`;
  }

  convertReferralRequest({
    date,
    instructions,
    send_to,
    urgent = false,
    physician_id,
  }: ReferralRequest): string {
    return `Referral Request (${CalendarUtils.format(date!)}, ${CalendarUtils.fromNow(
      date!,
    )}), Author ID: ${physician_id}, Urgent: ${urgent}, Send to:${
      send_to?.name ? `${send_to?.name}, ${send_to?.specialty}` : 'Pending selection'
    }\nInstructions: ${instructions}`;
  }

  convertPrescription(prescription: Prescription): string {
    const formattedMeds = prescription.medications
      .map((med) => CPPUtils.getPrescriptionMedicationSummary(med))
      .join('\n');
    return `Prescription (${CalendarUtils.format(prescription.date)}, ${CalendarUtils.fromNow(
      prescription.date,
    )})\n${formattedMeds}`;
  }

  convertImmunization(
    immunization: ImmunizationValue | AdministeredDrugValue,
    type: string = 'Immunization',
  ): string {
    const description = CPPUtils.getImmunizationDescription(immunization);
    return `${type} (${CalendarUtils.format(immunization.given_date!)}, ${CalendarUtils.fromNow(
      immunization.given_date!,
    )})\n${description}`;
  }

  getRecordsForSmartP(
    appointment: Appointment | undefined,
    patientHealthRecords: PatientData,
    patientReminders: Reminder[] = [],
  ): string {
    const today = moment().unix();
    const startDate = moment
      .unix(Math.min(today, appointment?.date || today))
      .startOf('day')
      .unix();
    let {
      documents = [],
      prescriptions = [],
      notes = [],
      referral_requests = [],
      immunizations = [],
      administered_drugs = [],
    } = patientHealthRecords;

    return [
      ...documents
        .filter(({ date }) => date! > startDate)
        .map((document, index) => this.convertDocument(document, index)),
      ...prescriptions
        .filter(({ date }) => date > startDate)
        .map((rx) => this.convertPrescription(rx)),
      ...notes.filter((note) => note.value.date! > startDate).map((note) => this.convertNote(note)),
      ...referral_requests.map((request) => this.convertReferralRequest(request)),
      ...patientReminders
        .filter(({ archived }) => !archived)
        .map((reminder) => this.convertReminder(reminder)),
      ...immunizations
        .filter(({ value }) => value.given_date! > startDate)
        .map(({ value }) => this.convertImmunization(value!)),
      ...administered_drugs
        .filter(({ value }) => value.given_date! > startDate)
        .map(({ value }) => this.convertImmunization(value!, 'Administered Drug')),
    ].join('\n\n');
  }

  getDummyPatientId(): string | undefined {
    return undefined; //'173e6740-082a-4e8e-9660-1e1ab63af39a';
  }

  async getDummyTranscription(appointmentId: string): Promise<AppointmentTranscription> {
    const transcription: AppointmentTranscription = {
      id: appointmentId,
      messages: [
        {
          from: 'Staff',
          text: "Thank you so much. OK, so it says a rash that you're having cough and constant sickness. Tell me how I can help you today.",
        },
        {
          from: 'Patient',
          text: 'So, um,',
        },
        {
          from: 'Patient',
          text: "I believe this is um like a long-term problem in our household. Uh after nausea, another, um, surgery, uh, in the August, the one related to the cancer, like a month after she got pneumonia, if you remember, and then she was prescribed by antibiotics and um took a couple of um X-rays. But then, I don't know whether it was related exactly to nausea.",
        },
        {
          from: 'Staff',
          text: 'Mhm.',
        },
        {
          from: 'Patient',
          text: 'in case or not, but all of us, like the kids and us, I mean, uh, we got pneumonia, like one after each other.',
        },
        {
          from: 'Patient',
          text: 'So, uh, I was the last one, and this is like',
        },
        {
          from: 'Patient',
          text: 'Almost 6 weeks ago that everything is done. I, I had uh antibiotics, but still the coughing is there.',
        },
        {
          from: 'Patient',
          text: "I know it takes time, but, uh, it's not.",
        },
        {
          from: 'Patient',
          text: 'I mean, it got even worse like 3 weeks ago, um, my son got a virus from perhaps school or something, so he has this cough. After that, I got this cough.',
        },
        {
          from: 'Patient',
          text: "I still have, uh, I still have it, but it's not as severe as before, but it's still really like, um, I don't know, it's just constant.",
        },
        {
          from: 'Patient',
          text: "And it's, and it's not dry, dry.",
        },
        {
          from: 'Patient',
          text: "I think, I think it's really important, um, given the history of what we went through to have an X-ray of his lungs and make sure that there's, because, uh, last year, my doctor didn't do it for me and this year I had a really bad pneumonia and they told me that um it was some residue from before as well in my lungs.",
        },
        {
          from: 'Patient',
          text: 'And um, so I, I really think that it would be really beneficial if you take a look at his lungs and an X-ray.',
        },
        {
          from: 'Staff',
          text: "OK, yeah, no, I appreciate that feedback. Tell me what other symptoms you're experiencing. Do you have a sore throat?",
        },
        {
          from: 'Patient',
          text: 'Look',
        },
        {
          from: 'Patient',
          text: 'No, no.',
        },
        {
          from: 'Staff',
          text: 'Do you have any fever?',
        },
        {
          from: 'Patient',
          text: 'No fever.',
        },
        {
          from: 'Staff',
          text: 'Do you have any night sweats?',
        },
        {
          from: 'Patient',
          text: 'No.',
        },
        {
          from: 'Staff',
          text: 'Any weight loss?',
        },
        {
          from: 'Patient',
          text: 'No.',
        },
        {
          from: 'Staff',
          text: 'How about runny nose, stuffy nose, nasal congestion?',
        },
        {
          from: 'Patient',
          text: 'Uh',
        },
        {
          from: 'Patient',
          text: 'I mean, not significantly, yeah, just like, yeah, not significantly runny nose from time to time.',
        },
        {
          from: 'Patient',
          text: 'Yes',
        },
        {
          from: 'Patient',
          text: 'Um,',
        },
        {
          from: 'Patient',
          text: 'But no other symptom as far as I.',
        },
        {
          from: 'Staff',
          text: 'How about ear fullness or ear pressure?',
        },
        {
          from: 'Patient',
          text: 'Yeah.',
        },
        {
          from: 'Patient',
          text: 'Sorry?',
        },
        {
          from: 'Staff',
          text: 'How about earfulness or ear pressure?',
        },
        {
          from: 'Patient',
          text: 'No, no.',
        },
        {
          from: 'Staff',
          text: 'Any changes to your taste or smell?',
        },
        {
          from: 'Patient',
          text: 'No',
        },
        {
          from: 'Staff',
          text: 'Mhm',
        },
        {
          from: 'Patient',
          text: "Uh, the reason that I'm, no.",
        },
        {
          from: 'Patient',
          text: 'No.',
        },
        {
          from: 'Staff',
          text: 'No? Any gastrointestinal symptoms? So nausea, vomiting, diarrhea?',
        },
        {
          from: 'Patient',
          text: 'No.',
        },
        {
          from: 'Staff',
          text: 'No. Are you coughing up blood?',
        },
        {
          from: 'Patient',
          text: 'No.',
        },
        {
          from: 'Staff',
          text: 'Do you have any shortness of breath?',
        },
        {
          from: 'Patient',
          text: 'Mm',
        },
        {
          from: 'Patient',
          text: 'Uh, no.',
        },
        {
          from: 'Patient',
          text: 'I mean, no, if, no.',
        },
        {
          from: 'Staff',
          text: 'No. How about itchy or watery eyes?',
        },
        {
          from: 'Patient',
          text: 'No.',
        },
        {
          from: 'Staff',
          text: 'No? Is it hard to clear the secretions in your chest?',
        },
        {
          from: 'Patient',
          text: 'Yeah, sometimes. Yeah.',
        },
        {
          from: 'Staff',
          text: 'Yeah',
        },
        {
          from: 'Staff',
          text: 'And no neck stiffness, no sensitivity to light or sound?',
        },
        {
          from: 'Patient',
          text: 'No, no.',
        },
        {
          from: 'Staff',
          text: 'How about joint pain or muscle aches?',
        },
        {
          from: 'Patient',
          text: 'Uh, no.',
        },
        {
          from: 'Staff',
          text: 'No. Did you get the flu or COVID shot this, this, uh, season?',
        },
        {
          from: 'Patient',
          text: 'Not yet, no.',
        },
        {
          from: 'Staff',
          text: 'Have you traveled recently?',
        },
        {
          from: 'Patient',
          text: 'Yes, we traveled.',
        },
        {
          from: 'Patient',
          text: "Um, we've been to the United States like for 3 weeks. I will travel by the end of the week again and yeah, but it's same place. I mean, like.",
        },
        {
          from: 'Staff',
          text: 'Mhm',
        },
        {
          from: 'Staff',
          text: 'Yeah, for sure, for sure. And then you said you got antibiotics.',
        },
        {
          from: 'Patient',
          text: 'Yes, for 5 days, I believe it was an antibiotics was prescribed by the walk-in clinic.',
        },
        {
          from: 'Staff',
          text: 'OK, OK, not from I say.',
        },
        {
          from: 'Patient',
          text: 'Oppos',
        },
        {
          from: 'Patient',
          text: 'No, no.',
        },
        {
          from: 'Staff',
          text: 'So',
        },
        {
          from: 'Staff',
          text: 'You took 2 tablets on the first day and 1 tablet every day after that.',
        },
        {
          from: 'Patient',
          text: 'Yes.',
        },
        {
          from: 'Patient',
          text: 'Yes.',
        },
        {
          from: 'Patient',
          text: 'Yes.',
        },
        {
          from: 'Staff',
          text: 'Mm',
        },
        {
          from: 'Patient',
          text: 'And it was, I mean,',
        },
        {
          from: 'Patient',
          text: 'Like, um,',
        },
        {
          from: 'Patient',
          text: "The doctor first, although he knew the situation in household, he tried, I mean, he hesitated to prescribe any antibiotics. I, I don't appreciate antibiotics either.",
        },
        {
          from: 'Patient',
          text: "Uh, so I wasn't looking forward to get like antibiotic, but like after a week or 10 days, then he said that no, it",
        },
        {
          from: 'Patient',
          text: "Obviously it's getting worse, so you have to take it.",
        },
        {
          from: 'Staff',
          text: 'Mhm, mhm, yeah, OK. And any history of smoking with you?',
        },
        {
          from: 'Patient',
          text: 'No',
        },
        {
          from: 'Staff',
          text: 'Well',
        },
        {
          from: 'Staff',
          text: 'And',
        },
        {
          from: 'Staff',
          text: 'And then you, in your history here, you used to have reactive airway disease or asthma before in your medical history. Can you tell me a little bit more about that?',
        },
        {
          from: 'Patient',
          text: 'Right.',
        },
        {
          from: 'Patient',
          text: "Uh, it's just a little bit it's not, um,",
        },
        {
          from: 'Patient',
          text: "Something significant, but it was like a, I don't know.",
        },
        {
          from: 'Patient',
          text: 'Um, first of all, it, I guess it started with the hay fever or the seasonal, um, allergy.',
        },
        {
          from: 'Patient',
          text: "I thought that this is the same, but perhaps it's just down the road, I realized that no, it's mixed up. It's a little bit like an asthma. The doctor like years ago.",
        },
        {
          from: 'Staff',
          text: 'Mhm',
        },
        {
          from: 'Patient',
          text: "But that's, and then uh it was a puffer for a while, using the puffer. Um, not much.",
        },
        {
          from: 'Patient',
          text: "Recently or not at all recently. Um, so I don't really, um, experience even the",
        },
        {
          from: 'Patient',
          text: "Hay fever this year. I mean, and it's not on, uh, it's only on spring. It's in spring, it's not like 2 times per year, like, uh, fall and spring is only spring. But not this year. This year, um,",
        },
        {
          from: 'Staff',
          text: 'Mhm',
        },
        {
          from: 'Patient',
          text: "I didn't experience any type of like uh allergy.",
        },
        {
          from: 'Staff',
          text: 'OK, OK. And so when did the symptoms start? You said 6 weeks ago?',
        },
        {
          from: 'Patient',
          text: '6, yeah, 6 weeks ago, but then, uh, I got to like 4 weeks ago, I got to the level that',
        },
        {
          from: 'Patient',
          text: 'It was constantly coughing, especially during the night, especially when I lay down.',
        },
        {
          from: 'Patient',
          text: "So it was that bad that we traveled, as I told you, um, even the other people in the house in different rooms, they, I mean, during the night, they got noticed that I'm constantly",
        },
        {
          from: 'Patient',
          text: 'Coffee.',
        },
        {
          from: 'Staff',
          text: 'Yeah. And have you used any medications so far or self-treated with anything so far?',
        },
        {
          from: 'Patient',
          text: 'No.',
        },
        {
          from: 'Patient',
          text: 'No, no.',
        },
        {
          from: 'Staff',
          text: 'OK, OK.',
        },
        {
          from: 'Staff',
          text: 'So 6 weeks ago would be at the beginning of December, right? Is that when this all started?',
        },
        {
          from: 'Patient',
          text: 'Yeah, or perhaps the 2nd week of December, but then by the end of December, almost end of December, it was the climax of coughing.',
        },
        {
          from: 'Staff',
          text: 'OK, OK.',
        },
        {
          from: 'Staff',
          text: 'OK, no problem. OK, so, and then just to review, just hay fever, your only allergies, no drug allergies?',
        },
        {
          from: 'Patient',
          text: 'No.',
        },
        {
          from: 'Staff',
          text: 'And then the only antibiotics in the last 3 months that you used is the azithromycin.',
        },
        {
          from: 'Patient',
          text: 'No.',
        },
        {
          from: 'Patient',
          text: 'Yes, yes.',
        },
        {
          from: 'Staff',
          text: 'And then you still take like Blaxton, maybe sometimes and no other medications every day?',
        },
        {
          from: 'Patient',
          text: 'No.',
        },
        {
          from: 'Staff',
          text: 'No, any recent surgeries, hospitalizations, updates to your medical history, just obstructive sleep apnea, prediabetes, chronic low back pain, and a history of reactive airway disease?',
        },
        {
          from: 'Patient',
          text: 'No, my',
        },
        {
          from: 'Patient',
          text: 'No',
        },
        {
          from: 'Patient',
          text: 'No',
        },
        {
          from: 'Patient',
          text: 'Uh, yeah.',
        },
        {
          from: 'Staff',
          text: 'Mm. Any family history that might be relevant today? Long?',
        },
        {
          from: 'Patient',
          text: 'Sorry, any any.',
        },
        {
          from: 'Staff',
          text: 'Any family history that might be relevant today outside of your wife, like family medical history that might be relevant?',
        },
        {
          from: 'Patient',
          text: 'No.',
        },
        {
          from: 'Staff',
          text: "OK. And you said non-smoker. OK. OK. So, um, it sounds like you maybe had, you probably had a pneumonia, if that's what the walk-in doctor diagnosed you with and treated you for. I will tell you that the cough can last 6 to 8 weeks, and that's a normal expected progression of pneumonia, especially in someone like over the age of 40 and into their",
        },
        {
          from: 'Patient',
          text: 'Yes.',
        },
        {
          from: 'Patient',
          text: 'Right.',
        },
        {
          from: 'Patient',
          text: 'Right.',
        },
        {
          from: 'Staff',
          text: "it's normal to have inflammation cause pneumonia is severe, right? Inflammation in your lungs, takes like a month or two to go away, and that cough, it's actually a good thing. We want to cough because when you cough, you're moving fluids around. You might not have a ton of fluids, but when fluids sit, that's when bacteria develops. So your body is coughing and moving air, and it's actually a good thing. But if the cough goes on longer than 8 weeks, that's when we want.",
        },
        {
          from: 'Patient',
          text: 'Right.',
        },
        {
          from: 'Staff',
          text: 'do a chest x-ray.',
        },
        {
          from: 'Patient',
          text: "OK, so, Alex, let me, um, perhaps I didn't.",
        },
        {
          from: 'Staff',
          text: 'So',
        },
        {
          from: 'Patient',
          text: 'I mean, I mislead you on willingly. So, you know, the pneumonia things was like long ago.',
        },
        {
          from: 'Patient',
          text: 'Late September or mid October.',
        },
        {
          from: 'Patient',
          text: 'And I',
        },
        {
          from: 'Patient',
          text: 'Yeah, I guess, yeah. Like even I would say even, yeah, it was October.',
        },
        {
          from: 'Patient',
          text: "OK? And then it was much better after the antibiotics and to the point that I thought that everything is gone. Then my kid gets I don't know, this coughing problem um from the daycare.",
        },
        {
          from: 'Patient',
          text: "And then I got again this like cough. I didn't have much cough at that time. So it's not 6 weeks, I'm not talking about this 6 weeks as the whole start of the pneumonia. The pneumonia part was like",
        },
        {
          from: 'Patient',
          text: '3 months ago.',
        },
        {
          from: 'Staff',
          text: 'OK, in October and November. And did you get better and then get sick again?',
        },
        {
          from: 'Patient',
          text: 'Yeah.',
        },
        {
          from: 'Patient',
          text: 'Yes, yes. When my, when my son got this coughing problem, just bringing the virus perhaps from the daycare. Yeah.',
        },
        {
          from: 'Staff',
          text: 'OK.',
        },
        {
          from: 'Staff',
          text: 'OK.',
        },
        {
          from: 'Staff',
          text: "OK, so that's OK. So still same thing because the kid, like, so you got, so we'll treat this as like this illness. So even this illness, we'll say it's like, you know, bronchitis or whatever, upper respiratory tract infection, pneumonia. It's normal to be coughing for like 4 to 6 weeks. If it gets to 8 weeks though, so I'll put a chest x-ray in your chart. If, wait another 1 to 2 weeks, I'm gonna give you some puffers today to try and manage it. If it's not gone away, let's do a chest X-ray. Cause we also want to be cautious.",
        },
        {
          from: 'Patient',
          text: 'OK.',
        },
        {
          from: 'Patient',
          text: 'OK',
        },
        {
          from: 'Patient',
          text: 'OK.',
        },
        {
          from: 'Staff',
          text: "Yeah, how much radiation we're exposing you to, and we won't do the chest X-ray. It's not necessary, but I'll put it in your chart and we'll treat you, and if it's not gone in a week or two, we'll do the chest X-ray.",
        },
        {
          from: 'Patient',
          text: 'Oh',
        },
        {
          from: 'Patient',
          text: 'OK, OK.',
        },
        {
          from: 'Staff',
          text: 'Do you want something for the runny nose as well?',
        },
        {
          from: 'Patient',
          text: "No, no, it's not that um uh that's a problem. I mean, it's just manageable.",
        },
        {
          from: 'Staff',
          text: 'OK. And then, are you still using the 1000 Bay Street Pharma pharmacy?',
        },
        {
          from: 'Patient',
          text: 'Yes.',
        },
        {
          from: 'Staff',
          text: 'Uh',
        },
        {
          from: 'Staff',
          text: 'And do you have a preference on imaging center?',
        },
        {
          from: 'Patient',
          text: 'Do we have?',
        },
        {
          from: 'Patient',
          text: 'The the Roda is pretty.',
        },
        {
          from: 'Staff',
          text: 'Broing.',
        },
        {
          from: 'Patient',
          text: 'Rosale.',
        },
        {
          from: 'Patient',
          text: 'But here is closer. I mean the Bay College.',
        },
        {
          from: 'Patient',
          text: 'Oh, the Bay College also is good.',
        },
        {
          from: 'Patient',
          text: '70 something.',
        },
        {
          from: 'Patient',
          text: '700 something.',
        },
        {
          from: 'Patient',
          text: "700, yes, it's just like a.",
        },
        {
          from: 'Staff',
          text: '790, yeah, the well Health.',
        },
        {
          from: 'Staff',
          text: "And they have walk-in check x-ray, so let's do that one.",
        },
        {
          from: 'Patient',
          text: 'Yeah',
        },
        {
          from: 'Patient',
          text: 'Yeah, thanks.',
        },
        {
          from: 'Staff',
          text: 'And then when you use puffers in the past, do you remember what color they were?',
        },
        {
          from: 'Patient',
          text: 'Um, this, um, bluish one with the, uh, I guess we have something here. I mean the blue puffer, the one that you put the capsule on top of it and uh.',
        },
        {
          from: 'Patient',
          text: 'Press.',
        },
        {
          from: 'Staff',
          text: 'OK, great. OK.',
        },
        {
          from: 'Staff',
          text: "OK. I'm gonna give you a different one. So it's called Symbicort. So it's a combination of a steroid.",
        },
        {
          from: 'Staff',
          text: 'Plus a bronchodilator.',
        },
        {
          from: 'Staff',
          text: 'So use it 1 to 2 puffs.',
        },
        {
          from: 'Patient',
          text: 'OK.',
        },
        {
          from: 'Staff',
          text: "Twice a day for 2 weeks. So start with like 2 puffs every day, twice a day for 2 weeks, then cut down to 1 puff twice a day, and then you can stop using it if you're totally better.",
        },
        {
          from: 'Patient',
          text: 'Sure.',
        },
        {
          from: 'Staff',
          text: "And you said you're good for the, for the nose, right? OK.",
        },
        {
          from: 'Patient',
          text: 'Yeah, yeah.',
        },
        {
          from: 'Staff',
          text: 'Mm',
        },
        {
          from: 'Staff',
          text: 'OK.',
        },
        {
          from: 'Staff',
          text: 'Just some other stuff to go over, do you guys have like a humidifier in your house?',
        },
        {
          from: 'Patient',
          text: 'Yes.',
        },
        {
          from: 'Staff',
          text: "OK, yeah. And like air clean, we've cleaned the, the air purifiers in the uh in the vents and stuff.",
        },
        {
          from: 'Staff',
          text: "Vacuuming, making sure there's no like pet, I know you guys don't have any pets, but.",
        },
        {
          from: 'Patient',
          text: "No, we don't, we don't. We have 2 kids. That's enough.",
        },
        {
          from: 'Staff',
          text: 'OK, yeah.',
        },
        {
          from: 'Staff',
          text: "Two kids in daycare, yeah, um, the other thing is get the COVID shot and the flu shot if you have a history of reactive airway disease, you're at risk to not get better as quickly. So just consider getting that shot and COVID shot this year. Um, wash your hands really well. Vitamin supplements, I know you already take a multivitamin with zinc. You can add vitamin C, but you can add some extra vitamin D in the winter that can boost your immune system and help you get better sooner. Um, but I put the X-ray requisition.",
        },
        {
          from: 'Patient',
          text: 'Yeah.',
        },
        {
          from: 'Patient',
          text: 'OK',
        },
        {
          from: 'Staff',
          text: "your chart, you'll just have to call and book it or walk in to book it, and then I sent the prescription off for the puffer.",
        },
        {
          from: 'Patient',
          text: 'OK',
        },
        {
          from: 'Patient',
          text: 'Thanks so much.',
        },
        {
          from: 'Staff',
          text: 'Hm.',
        },
        {
          from: 'Staff',
          text: 'Anything else you need today?',
        },
        {
          from: 'Patient',
          text: "I'm Alexandra.",
        },
        {
          from: 'Staff',
          text: 'On to us',
        },
        {
          from: 'Patient',
          text: "Big question for you. I'm so sorry. So, um, our son, um, had, um, ear pain for a very long time, uh, back in December, like late December, yeah, mid December, and uh I took him to his pediatrician, uh, 3 times in a week because he's very resilient, but he could not sleep.",
        },
        {
          from: 'Staff',
          text: 'Yeah.',
        },
        {
          from: 'Staff',
          text: 'Mhm',
        },
        {
          from: 'Patient',
          text: "Uh, because of the ear pain and he had high fever, um, and he, and, and she kept saying that no, there's no ear infection, nothing, nothing, nothing. The 4th day, I just, you know, gave up and took him to the um walk-in clinic at 1000 Bay and they saw that his ear has been bleeding inside and there is dried um blood in his ear and she said that",
        },
        {
          from: 'Staff',
          text: 'Mm',
        },
        {
          from: 'Staff',
          text: 'Mhm',
        },
        {
          from: 'Patient',
          text: 'Um',
        },
        {
          from: 'Patient',
          text: 'It has infection.',
        },
        {
          from: 'Staff',
          text: 'Oh, OK, OK.',
        },
        {
          from: 'Patient',
          text: 'And the reason I I gave up and took him uh to this other doctor was because he could not hear us.',
        },
        {
          from: 'Staff',
          text: 'Mhm',
        },
        {
          from: 'Patient',
          text: 'So she said that it has actually um affected the eardrum.',
        },
        {
          from: 'Staff',
          text: 'Yeah, the',
        },
        {
          from: 'Patient',
          text: "I don't wanna, so she gave us antibiotics. Um, he's better now, but he still cannot hear well. So, um, I don't wanna take her, take him to the pediatrician anymore, like I gave up on her. Um, can I bring him to you and take a look?",
        },
        {
          from: 'Staff',
          text: 'Mhm',
        },
        {
          from: 'Staff',
          text: 'Yeah',
        },
        {
          from: 'Staff',
          text: 'Yeah, no, I see tricks, yeah, mhm.',
        },
        {
          from: 'Patient',
          text: 'Yes, because I wanna check his hearing, basically.',
        },
        {
          from: 'Staff',
          text: 'Yeah, that.',
        },
        {
          from: 'Patient',
          text: 'I, mhm.',
        },
        {
          from: 'Patient',
          text: 'Yeah, OK, so.',
        },
        {
          from: 'Staff',
          text: "in office and then we'll see what the tympanic membrane looks like and then we'll send him for testing or with an ENT if he needs it.",
        },
        {
          from: 'Patient',
          text: "OK, because I don't have membership for him from your clinic. Can I pay like as we go with for him?",
        },
        {
          from: 'Staff',
          text: 'OK',
        },
        {
          from: 'Staff',
          text: 'Sure, if you want to pay $100 per visit, yeah, you can do him as a walk-in, yeah.',
        },
        {
          from: 'Patient',
          text: 'Yes. OK. All right.',
        },
        {
          from: 'Staff',
          text: 'Yeah.',
        },
        {
          from: 'Staff',
          text: 'Yeah, sorry, poor guy.',
        },
        {
          from: 'Patient',
          text: 'Thank you so much.',
        },
        {
          from: 'Patient',
          text: 'Uh',
        },
        {
          from: 'Patient',
          text: 'Yes, thank you. Bye-bye.',
        },
        {
          from: 'Staff',
          text: 'Thanks, bye.',
        },
        {
          from: 'Patient',
          text: 'Bye. Have a good day.',
        },
      ],
    };
    return new Promise((resolve) => setTimeout(() => resolve(transcription), 2000));
  }
}

export default new AIUtils();
